// Recettes (lot L2-A) : caisses de régie (H4-07, D-014), relevés et rapprochements (H4-09),
// anomalies (H4-11). Un reçu provisoire numéroté est émis par le régisseur de la caisse ; la
// clôture journalière totalise les reçus, enregistre l'écart constaté et produit un bordereau ; le
// financier vérifie et transforme les reçus en paiements confirmés (une quittance chacun). Une
// anomalie est un constat à instruire, sans conséquence automatique.
import { z } from 'zod';
import { CANAUX_PAIEMENT } from './recettes-paiements';

const uuid = z.string().uuid('Identifiant invalide');
const jour = z.iso.date('Date AAAA-MM-JJ attendue');

export const ETATS_CAISSE_RECU = ['emis', 'cloture', 'verifie', 'annule'] as const;

export const schemaCreationCaisse = z.strictObject({
  libelle: z.string().trim().min(2).max(200),
  lieu: z.string().trim().min(2).max(200),
  regisseur_appartenance_id: uuid,
});

export const schemaRecuCaisse = z.strictObject({
  creance_id: uuid,
  montant: z.number().int().positive(),
});

export const schemaAnnulationRecu = z.strictObject({ motif: z.string().trim().min(3).max(1000) });

export const schemaClotureCaisse = z.strictObject({
  /** Journée clôturée (Libreville), défaut aujourd'hui. */
  date: jour.optional(),
  montant_constate: z.number().int().min(0),
});

export const schemaFiltreRecus = z.object({
  etat: z.enum(ETATS_CAISSE_RECU).optional(),
  date: jour.optional(),
});

// --- Rapprochements -------------------------------------------------------------------

/** Champs du formulaire d'import d'un relevé (le fichier CSV est la partie « fichier »). */
export const schemaImportReleve = z
  .object({
    canal: z.enum(CANAUX_PAIEMENT),
    prestataire: z.string().regex(/^[a-z][a-z0-9_]{1,62}$/).optional(),
    periode_debut: jour,
    periode_fin: jour,
  })
  .refine((v) => v.periode_fin >= v.periode_debut, { message: 'La fin suit le début', path: ['periode_fin'] });

export const schemaRapprochementManuel = z.strictObject({
  paiement_id: uuid,
  motif: z.string().trim().min(3).max(1000),
});

export const schemaParametresLigneReleve = z.object({ id: uuid, ligneId: uuid });

/** Colonnes attendues du relevé CSV (en-tête obligatoire, séparateur point-virgule ou virgule). */
export const COLONNES_RELEVE = ['reference', 'montant', 'date'] as const;

// --- Anomalies ------------------------------------------------------------------------

export const TYPES_ANOMALIE = {
  paiement_non_confirme: 'Paiement non confirmé depuis plus de 48 heures',
  annulations_repetees: 'Plus de trois annulations par un même agent en sept jours',
  doublon_reference: 'Doublon de référence du prestataire',
  tarif_sans_acte: 'Tarif approuvé sans acte d\'approbation',
  remise_excessive: 'Remise supérieure au seuil',
  annulation_sans_piece: 'Créance annulée sans pièce justificative',
  ecart_caisse: 'Écart de caisse à la clôture',
  ecart_rapprochement: 'Écart de rapprochement',
  notification_tardive: 'Confirmation reçue après l\'expiration du paiement',
  notification_contradictoire: 'Notifications contradictoires du prestataire',
  trop_percu: 'Montant reçu supérieur au reste dû',
} as const;
export type TypeAnomalie = keyof typeof TYPES_ANOMALIE;

export const ETATS_ANOMALIE = ['ouverte', 'en_instruction', 'expliquee', 'confirmee'] as const;
export type EtatAnomalie = (typeof ETATS_ANOMALIE)[number];

/** Délai au-delà duquel un paiement en attente devient une anomalie (H4-11). */
export const DELAI_PAIEMENT_NON_CONFIRME_HEURES = 48;

export const schemaFiltreAnomalies = z.object({
  etat: z.enum(ETATS_ANOMALIE).optional(),
  type: z.enum(Object.keys(TYPES_ANOMALIE) as [TypeAnomalie, ...TypeAnomalie[]]).optional(),
  ouvertes: z.enum(['oui', 'non']).optional(),
});

export const schemaInstructionAnomalie = z.strictObject({ commentaire: z.string().trim().max(2000).optional() });

export const schemaConclusionAnomalie = z.strictObject({
  etat: z.enum(['expliquee', 'confirmee']),
  conclusion: z.string().trim().min(3).max(4000),
});
